import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MirrorPresenceState } from "@/components/mirror/MirrorPresenceConfig";
import { getMirrorStateAnimation } from "@/config/avatarAnimations";

// Liveness Group B — explicit Mixamo state machine. Caller (MirrorChat)
// passes the current `mirrorState` as input; the hook factors in its own
// internal `isIdleBreak` rotation and returns `effectiveAnimationState`
// (the resolved state to render) plus `stateAnimations` (the 5 URLs the
// state machine will pre-load and crossfade between).
export type MirrorAnimationState =
  | "idle"
  | "idleBreak"
  | "listening"
  | "thinking"
  | "speaking";

const STATE_ANIMATIONS: Record<MirrorAnimationState, string> = {
  idle: "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/avatar-animations/Idle%20Oficial%20Mirror%201.fbx",
  idleBreak: "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/avatar-animations/weight%20shift.fbx",
  listening: "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/avatar-animations/Telling%20A%20Secret.fbx",
  thinking: "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/avatar-animations/thoughtful%20head%20shake.fbx",
  speaking: "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/avatar-animations/Talking.fbx",
};

interface UseMirrorStateOptions {
  isVoiceListening: boolean;
  isTTSSpeaking: boolean;
  // Optional explicit state machine input. When provided, the hook also
  // returns `stateAnimations` + `effectiveAnimationState`. Existing callers
  // (no mirrorState) keep receiving the legacy `animationUrl` shape only.
  mirrorState?: MirrorAnimationState;
}

interface UseMirrorStateReturn {
  presenceState: MirrorPresenceState;
  animationUrl: string;
  startThinking: () => void;
  goIdle: () => void;
  // Group B opt-in surface — only populated when mirrorState arg is passed.
  stateAnimations?: Record<MirrorAnimationState, string>;
  effectiveAnimationState?: MirrorAnimationState;
}

const IDLE_BREAK_DELAY_MS = 30_000;
const IDLE_BREAK_DURATION_MS = 2_500;

export function useMirrorState(options: UseMirrorStateOptions): UseMirrorStateReturn {
  const { isVoiceListening, isTTSSpeaking, mirrorState } = options;

  const [presenceState, setPresenceState] = useState<MirrorPresenceState>("idle");
  const [isIdleBreak, setIsIdleBreak] = useState(false);

  const idleBreakTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleBreakDurationRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stateRef = useRef<MirrorPresenceState>(presenceState);
  useEffect(() => {
    stateRef.current = presenceState;
  }, [presenceState]);

  // Effect — sync voice listening
  useEffect(() => {
    if (isVoiceListening) {
      if (stateRef.current === "idle") {
        setPresenceState("listening");
      }
    } else {
      if (stateRef.current === "listening") {
        setPresenceState("idle");
      }
    }
  }, [isVoiceListening]);

  // Effect — sync TTS speaking
  useEffect(() => {
    if (isTTSSpeaking) {
      if (stateRef.current !== "speaking") {
        setPresenceState("speaking");
      }
    } else {
      if (stateRef.current === "speaking") {
        setPresenceState("idle");
      }
    }
  }, [isTTSSpeaking]);

  // Effect — schedule idleBreak after sustained idle
  useEffect(() => {
    if (idleBreakTimerRef.current) {
      clearTimeout(idleBreakTimerRef.current);
      idleBreakTimerRef.current = null;
    }

    if (presenceState === "idle") {
      idleBreakTimerRef.current = setTimeout(() => {
        setIsIdleBreak(true);
        idleBreakTimerRef.current = null;
      }, IDLE_BREAK_DELAY_MS);
    } else {
      setIsIdleBreak(false);
    }

    return () => {
      if (idleBreakTimerRef.current) {
        clearTimeout(idleBreakTimerRef.current);
        idleBreakTimerRef.current = null;
      }
    };
  }, [presenceState]);

  // Effect — auto-close idleBreak after one cycle
  useEffect(() => {
    if (idleBreakDurationRef.current) {
      clearTimeout(idleBreakDurationRef.current);
      idleBreakDurationRef.current = null;
    }
    if (isIdleBreak) {
      idleBreakDurationRef.current = setTimeout(() => {
        setIsIdleBreak(false);
        idleBreakDurationRef.current = null;
      }, IDLE_BREAK_DURATION_MS);
    }

    return () => {
      if (idleBreakDurationRef.current) {
        clearTimeout(idleBreakDurationRef.current);
        idleBreakDurationRef.current = null;
      }
    };
  }, [isIdleBreak]);

  const startThinking = useCallback(() => {
    setPresenceState("thinking");
  }, []);

  const goIdle = useCallback(() => {
    setPresenceState("idle");
  }, []);

  const animationUrl = useMemo(() => {
    if (isIdleBreak) return getMirrorStateAnimation("idleBreak");
    return getMirrorStateAnimation("idle");
  }, [isIdleBreak]);

  // Group B state machine outputs — only populated when caller opted in
  // via the `mirrorState` arg. Leaves legacy callers with the same return
  // shape (stateAnimations / effectiveAnimationState absent).
  const stateAnimations = mirrorState !== undefined ? STATE_ANIMATIONS : undefined;
  const effectiveAnimationState =
    mirrorState !== undefined
      ? mirrorState === "idle" && isIdleBreak
        ? "idleBreak"
        : mirrorState
      : undefined;

  return {
    presenceState,
    animationUrl,
    startThinking,
    goIdle,
    stateAnimations,
    effectiveAnimationState,
  };
}
